import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { generateConfig } from "../../utils/api";

export default function ConfirmAppointment() {
  const { id } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmationStatus, setConfirmationStatus] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const { config, baseURL } = generateConfig();
        const response = await axios.get(
          `${baseURL}/api/client-portal/fetch-appointment/${id}`,
          config
        );
        setAppointment(response.data);

        // If already confirmed, skip to confirmation status
        if (response.data.status?.includes("Confirmed")) {
          setConfirmationStatus("success");
        }

        setLoading(false);
      } catch (err) {
        setError("Unable to fetch appointment details");
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [id]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const { config, baseURL } = generateConfig();
      await axios.post(
        `${baseURL}/api/client-portal/confirm-appointment/${id}`,
        {},
        config
      );
      setConfirmationStatus("success");
    } catch (err) {
      setConfirmationStatus("error");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-300 border-t-slate-600"></div>
          <span className="text-slate-600 text-sm">Loading appointment...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-50 rounded-full mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Unable to Load Appointment
          </h2>
          <p className="text-sm text-gray-600 text-center">{error}</p>
        </div>
      </div>
    );
  }

  if (confirmationStatus === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-50 rounded-full mb-6">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-3">
            Appointment Confirmed!
          </h2>
          <p className="text-sm text-gray-600 text-center leading-relaxed">
            Thank you for confirming your appointment. We look forward to seeing
            you on{" "}
            <span className="font-medium text-gray-900">
              {new Date(appointment?.start).toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </span>{" "}
            at{" "}
            <span className="font-medium text-gray-900">
              {new Date(appointment?.start).toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
            .
          </p>
        </div>
      </div>
    );
  }

  if (confirmationStatus === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-50 rounded-full mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Confirmation Failed
          </h2>
          <p className="text-sm text-gray-600 text-center mb-4">
            We were unable to confirm your appointment. Please try again or
            contact us directly.
          </p>
          <button
            onClick={() => setConfirmationStatus(null)}
            className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Get clinician display name
  const getClinicianName = () => {
    if (appointment?.clinician?.calendarDisplayName) {
      return appointment.clinician.calendarDisplayName;
    }
    if (appointment?.clinician?.firstName && appointment?.clinician?.lastName) {
      return `${appointment.clinician.firstName} ${appointment.clinician.lastName}`;
    }
    return "Your Provider";
  };

  // Format clinic address
  const getClinicAddress = () => {
    const address = appointment?.clinic?.address;
    if (!address) return "";

    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipCode) parts.push(address.zipCode);

    return parts.join(", ");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-start space-x-4 mb-8">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-gray-900 mb-1">
                Confirm Your Appointment
              </h1>
              <p className="text-sm text-gray-500">
                Please review the details below and confirm your attendance
              </p>
            </div>
          </div>

          {appointment && (
            <div className="space-y-6">
              {/* Appointment Details */}
              <div className="space-y-4">
                {/* Date & Time */}
                <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-shrink-0 w-5 h-5 text-slate-400 mt-0.5">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Date & Time
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(appointment.start).toLocaleDateString(
                        undefined,
                        {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(appointment.start).toLocaleTimeString(
                        undefined,
                        {
                          hour: "numeric",
                          minute: "2-digit",
                        }
                      )}{" "}
                      -{" "}
                      {new Date(appointment.end).toLocaleTimeString(undefined, {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>

                {/* Client */}
                <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-shrink-0 w-5 h-5 text-slate-400 mt-0.5">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Client
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {appointment.client?.firstName}{" "}
                      {appointment.client?.lastName}
                    </p>
                  </div>
                </div>

                {/* Provider */}
                <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-shrink-0 w-5 h-5 text-slate-400 mt-0.5">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Provider
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {getClinicianName()}
                    </p>
                    {appointment.clinician?.jobTitle && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {appointment.clinician.jobTitle}
                      </p>
                    )}
                  </div>
                </div>

                {/* Service */}
                <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-shrink-0 w-5 h-5 text-slate-400 mt-0.5">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Service
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {appointment.service?.description || appointment.title}
                    </p>
                    {appointment.service?.duration && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {appointment.service.duration} minutes
                      </p>
                    )}
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-shrink-0 w-5 h-5 text-slate-400 mt-0.5">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Location
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {appointment.clinic?.name}
                    </p>
                    {getClinicAddress() && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {getClinicAddress()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-6 border-t border-slate-200">
                <button
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2">
                  {confirming ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Confirming...</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Confirm Appointment</span>
                    </>
                  )}
                </button>

                <div className="text-center">
                  <p className="text-xs text-gray-500">
                    Need to make changes?{" "}
                    <span className="font-medium text-gray-700">
                      Call us at 905-875-3345
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
